import { SceneEditorAppWrapper } from "oc-scene-editor/ui/SceneEditorAppWrapper";
import { SavingContextProvider } from "../hooks/UseSaving";
import * as Dialogs from "oc-ui/dialogs/Dialogs";
import { Masseditor } from "../pages/Masseditor";
import { Header } from "./Header";
import { Cookiebanner, isCookieSupressed } from "../components/Cookiebanner";
import { PreviewPicture } from "./PreviewPictures";
import { FertigDialog } from "../components/zauneditor/FertigDialog";
import Messages from "../commons/Messages";
import { SkipLink } from "../components/SkipLink";
import { getWarnungWichtigkeitColor, mergeWarnungenByText } from "../arrangement/Warnung";
import type { MergedWarnung } from "../arrangement/Warnung";

type WarnungVorschau = MergedWarnung;

const TORKONF_WARNUNG_PREFIX_ICON = "⚠ ";

function getPreviewWarnungen(): WarnungVorschau[] {
  return mergeWarnungenByText(APP.arrangement?.warnungen ?? []);
}

function PreviewWarnungen() {
  const warnungen = getPreviewWarnungen();
  if (warnungen.length === 0) {
    return null;
  }

  return (
    <section className="previewWarnings" aria-label="Warnungen">
      <ul>
        {warnungen.map((warnung) => {
          const color = getWarnungWichtigkeitColor(warnung.wichtigkeit).text;
          const hasDialogConfig = warnung.dialogConfig != null;

          const openWarnungDialog = () => {
            if (!warnung.dialogConfig) {
              return;
            }
            Dialogs.showWarningDetails(warnung.dialogConfig);
          };

          return (
            <li
              key={warnung.text}
              className={
                "previewWarning " + warnung.wichtigkeit + (hasDialogConfig ? " hasDetails" : "")
              }
              role={hasDialogConfig ? "button" : undefined}
              tabIndex={hasDialogConfig ? 0 : undefined}
              onClick={hasDialogConfig ? openWarnungDialog : undefined}
              onKeyDown={
                hasDialogConfig
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openWarnungDialog();
                      }
                    }
                  : undefined
              }
            >
              <span className="previewWarningNumber">
                <span className="previewWarningPrefixIcon" style={{ color }}>
                  {TORKONF_WARNUNG_PREFIX_ICON}
                </span>
                {warnung.index}.
              </span>
              <span
                className="previewWarningText"
                dangerouslySetInnerHTML={{ __html: warnung.text }}
              ></span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function Index() {
  const Page = APP.pageClass;
  const cookies = APP.richMetadata?.getCookieData();
  const urlParams = new URLSearchParams(window.location.search);
  const isTest = urlParams.has("test");

  const previewFrontLabel =
    APP.optionMenu?.getCurrentKey() == "Abschluss" ? I18N("3D-Ansicht") : I18N("Vorderansicht");

  return (
    <>
      <SavingContextProvider>
        <SkipLink targetId="optionMenuContent" focusFirstInteractive />

        <div id="grid-header" role="banner">
          <Header />
        </div>
        <div
          id="grid-menu"
          className="fullView"
          role="navigation"
          aria-label={I18N("Konfigurationsmenü")}
        ></div>

        <main id="grid-content" className="window-col" aria-label={I18N("Hauptinhalt")}>
          {Page && (
            <Masseditor
              key={APP.pageShowCounter}
              ref={(r) => {
                if (r != null) {
                  APP.pageInstance = r;
                  APP.pageInstanceCounter++;
                }
              }}
            />
          )}
        </main>
        <aside id="grid-views" role="region" aria-label={I18N("3D-Vorschau")}>
          <SceneEditorAppWrapper
            sceneNode={APP.sceneNode}
            isAdmin={APP.settings.userRole == "admin"}
          >
            <PreviewPicture name={"Front"} label={previewFrontLabel}>
              <div className="stage"></div>
            </PreviewPicture>
            <PreviewPicture name={"Back"} label={I18N("Rückansicht")}></PreviewPicture>
          </SceneEditorAppWrapper>
          <PreviewWarnungen />
        </aside>

        {Page && <FertigDialog onClose={() => APP.dirty()} />}
        <Dialogs.DialogContainer />
      </SavingContextProvider>
      <Messages managerObject={APP} />
      {!isTest && cookies && !isCookieSupressed() && (
        <Cookiebanner cookies={cookies} storageKey={APP.cookieStorageKey} />
      )}
    </>
  );
}
